import { isPast } from 'date-fns';
import type { GetServerSidePropsContext } from 'next';

import {
  COUNTRY_CODES,
  COUNTRY_COOKIE_NAME,
} from '../../../constants/locations';
import {
  PRODUCT_TAG_COMING_SOON,
  PRODUCT_TAG_SOLD_OUT,
} from '../../../constants/product';
import { getDrawStage } from '../../../hooks/useDrawStage';
import { ProductSourceBackend } from '../../../providers/ProductProvider';
import {
  GeneratedDrawType,
  GeneratedProductReleaseType,
} from '../../../types/generated';
import { CountryCode } from '../../../types/Locations';
import { ShopifyProductPurchaseDetails } from '../../../types/Purchasing';
import { isPublicVariantTitle } from '../../../utils/product';
import { shopifyGraphqlIdToAdminId } from '../../../utils/shopifyIds';
import { DrawByProductId } from '../../draws/types';
import { resolveCheckoutFlowType } from './flowType';

type GateArgs = {
  draw?: DrawByProductId | null;
  product: ProductSourceBackend;
};

const drawGate = (
  product: ProductSourceBackend,
  draw: DrawByProductId | null | undefined,
): boolean => {
  if (!draw) return false;
  if (isPast(new Date(draw.closeAt))) return false;

  return (
    resolveCheckoutFlowType({
      releaseType: product.releaseType,
      stage: getDrawStage(draw),
    }) !== null
  );
};

export const buyGate = (product: ProductSourceBackend): boolean => {
  if (product.tags.includes(PRODUCT_TAG_COMING_SOON)) return false;
  if (product.closeAt && isPast(new Date(product.closeAt))) return false;
  if (product.tags.includes(PRODUCT_TAG_SOLD_OUT)) return false;

  return product.variants.some((variant) => {
    return variant.available && isPublicVariantTitle(variant.title);
  });
};

export const privateVariantGate = (
  details: ShopifyProductPurchaseDetails | null,
  variantId: string,
): boolean => {
  return Boolean(
    details?.available &&
    details.requiresUnlockCode &&
    details.shopifyVariantId &&
    shopifyGraphqlIdToAdminId(details.shopifyVariantId) === variantId,
  );
};

// The buyer's country from the same sources the browser uses: their saved
// country cookie, else Vercel's geo header. Null when neither holds a known
// country, and the region check then lets the sale through.
export const requestCountry = ({
  cookies,
  headers,
}: Pick<
  GetServerSidePropsContext['req'],
  'cookies' | 'headers'
>): CountryCode | null => {
  const header = headers['x-vercel-ip-country'];
  const candidates = [
    cookies[COUNTRY_COOKIE_NAME],
    Array.isArray(header) ? header[0] : header,
  ];

  return (
    (candidates.find((candidate) =>
      COUNTRY_CODES.includes(candidate as CountryCode),
    ) as CountryCode | undefined) ?? null
  );
};

export const regionGate = (
  details: ShopifyProductPurchaseDetails | null,
  country: CountryCode | null,
): boolean => {
  return !(country && details?.excludeFromRegions.includes(country));
};

const auctionGate = (
  product: ProductSourceBackend,
  draw: DrawByProductId | null | undefined,
): boolean => {
  if (draw?.drawType !== GeneratedDrawType.Auction) return false;
  if (draw.multisetID) return false;

  return drawGate(product, draw);
};

export const checkoutGate = ({ draw, product }: GateArgs): boolean => {
  switch (product.releaseType) {
    case GeneratedProductReleaseType.Auction:
      return auctionGate(product, draw);
    case GeneratedProductReleaseType.Draw:
      if (draw?.drawType === GeneratedDrawType.Auction) return false;

      return drawGate(product, draw);
    case GeneratedProductReleaseType.Public:
    case GeneratedProductReleaseType.Timed:
      return buyGate(product);
    default:
      return false;
  }
};
